import type SmoothScroll from "smooth-scroll";
import { BREAK_WIDTH_PC, GALLERY_QTY_PC, GALLERY_QTY_SP, IS_DEBUG } from "./_constants";
import { PakutasoImage, PakutasoTag, PakutasoTagId, PAKUTASO_TAG_ID_ALL } from "./_pakutaso_types";


export default class PakutasoGallery {
  currentTagId: PakutasoTagId = PAKUTASO_TAG_ID_ALL;

  currentIndex = 0;

  pakutasoImageList: PakutasoImage[];

  pakutasoTagList: PakutasoTag[];

  blockElement: Element;

  smoothScroll: SmoothScroll;

  constructor(blockElement: Element, smoothScroll: SmoothScroll) {
    if (!window.pakutasoImageList) {
      throw new Error("window.pakutasoImageList is null");
    }
    this.blockElement = blockElement;
    this.pakutasoTagList = window.pakutasoTagList;
    this.pakutasoImageList = window.pakutasoImageList;
    this.smoothScroll = smoothScroll;

    this.updateTagList();
    this.updateImageList();
    this.setupNav();
    this.updateNav();

    window.addEventListener("resize", () => {
      this.onResize();
    });

  }

  // eslint-disable-next-line class-methods-use-this
  toIconUrl(iconFilename: string): string {
    return `./assets/img/pakutaso-gallery/icons/${iconFilename}`;
  }

  // eslint-disable-next-line class-methods-use-this
  toImageUrl(imageFilename: string): string {
    return `./assets/img/pakutaso-gallery/images/${imageFilename}`;
  }

  updateTagList() {
    const tagListElement = this.blockElement.querySelector(".p-pakutaso-gallery__tag-list");
    if (!tagListElement) {
      throw new Error("tagListElement is null");
    }
    while (tagListElement.firstChild) {
      tagListElement.removeChild(tagListElement.firstChild);
    }

    const makeTagElement = (tag: PakutasoTag, isDisabled: boolean) => {
      const tagElement = document.createElement("div");
      tagElement.classList.add("p-pakutaso-gallery__tag");
      if (isDisabled) {
        tagElement.classList.add("p-pakutaso-gallery__tag--disabled");
      }
      const icon = document.createElement("img");
      icon.classList.add("p-pakutaso-gallery__tag-icon");
      if (isDisabled) {
        icon.src = this.toIconUrl(tag.iconDisabled);
      } else {
        icon.src = this.toIconUrl(tag.icon);
      }
      icon.alt = "";
      tagElement.appendChild(icon);
      const tagTextElement = document.createElement("span");
      tagTextElement.classList.add("p-pakutaso-gallery__tag-text");
      tagTextElement.textContent = tag.id;
      tagElement.appendChild(tagTextElement);
      if (!isDisabled) {
        tagElement.addEventListener("click", () => {
          this.onClickTag(tag.id);
        });
      }
      return tagElement;
    };
    for (const tag of this.pakutasoTagList) {
      const tagElement = makeTagElement(tag, this.currentTagId === tag.id);
      tagListElement.appendChild(tagElement);
    }
  }

  getFilteredImageList() {
    let filteredImageList: PakutasoImage[] = this.pakutasoImageList;
    if (this.currentTagId !== PAKUTASO_TAG_ID_ALL) {
      const tagId = this.currentTagId;
      filteredImageList = filteredImageList.filter((image) => image.tagIdList.includes(tagId));
    }
    return filteredImageList;
  }

  // eslint-disable-next-line class-methods-use-this
  getGalleryQty() {

    const isPC = !!window.matchMedia(`(min-width: ${BREAK_WIDTH_PC}px)`).matches;

    if (isPC) {
      return GALLERY_QTY_PC;
    }
    return GALLERY_QTY_SP;


  }

  updateImageList() {
    const imageListElement = this.blockElement.querySelector(".p-pakutaso-gallery__image-list");
    if (!imageListElement) {
      throw new Error("imageListElement is null");
    }
    while (imageListElement.firstChild) {
      imageListElement.removeChild(imageListElement.firstChild);
    }
    const makeImageElement = (image: PakutasoImage) => {

      const thumbnailUrl = this.toImageUrl(image.thumbnail);

      const imageElement = document.createElement("a");
      imageElement.href = image.url;
      imageElement.target = "_blank";
      imageElement.classList.add("p-pakutaso-gallery__image");


      const pictureElement = document.createElement("picture");
      imageElement.appendChild(pictureElement);

      const sourceElement = document.createElement("source");
      sourceElement.type = "image/webp";
      const thumbnailWebp = thumbnailUrl.replace(/.jpg$/, ".webp");
      sourceElement.srcset = encodeURI(thumbnailWebp);

      pictureElement.appendChild(sourceElement);

      const thumbnailElement = document.createElement("img");
      thumbnailElement.classList.add("p-pakutaso-gallery__image-thumbnail");
      thumbnailElement.src = encodeURI(thumbnailUrl);
      thumbnailElement.alt = image.title;
      thumbnailElement.loading = "lazy";
      pictureElement.appendChild(thumbnailElement);

      const titleElement = document.createElement("div");
      titleElement.classList.add("p-pakutaso-gallery__image-title");
      titleElement.textContent = image.title;
      imageElement.appendChild(titleElement);

      return imageElement;
    };

    const filteredImageList = this.getFilteredImageList();

    const imageList: PakutasoImage[] = [];
    const galleryQty = this.getGalleryQty();
    const fromIndex = this.currentIndex * galleryQty;
    const toIndex = (this.currentIndex + 1) * galleryQty;
    if (IS_DEBUG) {
      console.log(fromIndex, toIndex);
    }
    for (let i = 0; i < filteredImageList.length; i += 1) {
      const image = filteredImageList[i];
      if (fromIndex <= i && i < toIndex) {
        imageList.push(image);
      }
    }

    for (const image of imageList) {
      const imageElement = makeImageElement(image);
      imageListElement.appendChild(imageElement);
    }


  }

  setupNav() {
    const navPrevElement = this.blockElement.querySelector(".p-pakutaso-gallery__nav-prev");
    if (!navPrevElement) {
      throw new Error("navPrevElement is null");
    }
    const navNextElement = this.blockElement.querySelector(".p-pakutaso-gallery__nav-next");
    if (!navNextElement) {
      throw new Error("navNextElement is null");
    }

    navPrevElement.addEventListener("click", () => {
      this.onClickPrev();
    });
    navNextElement.addEventListener("click", () => {
      this.onClickNext();
    });

  }

  updateNav() {

    const navPrevElement = this.blockElement.querySelector(".p-pakutaso-gallery__nav-prev");
    if (!navPrevElement) {
      throw new Error("navPrevElement is null");
    }
    const navNextElement = this.blockElement.querySelector(".p-pakutaso-gallery__nav-next");
    if (!navNextElement) {
      throw new Error("navNextElement is null");
    }
    const navPageElement = this.blockElement.querySelector(".p-pakutaso-gallery__nav-page");
    if (!navPageElement) {
      throw new Error("navPageElement is null");
    }

    const filteredImageList = this.getFilteredImageList();
    const galleryQty = this.getGalleryQty();
    const qty = Math.ceil(filteredImageList.length / galleryQty);

    navPageElement.textContent = `${this.currentIndex + 1} / ${qty}`;


    const prevDisabled = "p-pakutaso-gallery__nav-prev--disabled";
    navPrevElement.classList.remove(prevDisabled);
    if (!(this.currentIndex > 0)) {
      navPrevElement.classList.add(prevDisabled);
    }
    const nextDisabled = "p-pakutaso-gallery__nav-next--disabled";
    navNextElement.classList.remove(nextDisabled);
    if (!(this.currentIndex < qty - 1)) {
      navNextElement.classList.add(nextDisabled);
    }

  }

  onClickTag(tagId: PakutasoTagId) {
    if (IS_DEBUG) {
      console.log(`onClickTag ${tagId}`);
    }
    this.currentTagId = tagId;
    this.currentIndex = 0;
    this.updateTagList();
    this.updateImageList();
    this.updateNav();
    this.smoothScroll.animateScroll(this.blockElement);
  }

  onClickPrev() {
    if (IS_DEBUG) {
      console.log(`onClickPrev`);
    }
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.updateImageList();
      this.updateNav();
      this.smoothScroll.animateScroll(this.blockElement);
    }
  }

  onClickNext() {
    if (IS_DEBUG) {
      console.log(`onClickNext`);
    }
    const filteredImageList = this.getFilteredImageList();
    const galleryQty = this.getGalleryQty();
    const qty = Math.ceil(filteredImageList.length / galleryQty);
    if (this.currentIndex < qty - 1) {
      this.currentIndex += 1;
      this.updateImageList();
      this.updateNav();
      this.smoothScroll.animateScroll(this.blockElement);
    }
  }

  onResize() {
    if (GALLERY_QTY_PC === GALLERY_QTY_SP) {
      // 個数が同じなら更新しない。
      return;
    }
    this.updateImageList();
    this.updateNav();
  }
}

